import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "@/resources/requestInfo";
import {
  ITournament,
  ITournamentApplication,
  ICreateTournamentData,
  IApplyTournamentData,
} from "./types";

export const fetchGetTournamentTypes = createAsyncThunk<string[], void>(
  "tournaments/fetchGetTournamentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/tournaments/types");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка получения типов турниров"
      );
    }
  }
);

export const fetchGetAvailableTournaments = createAsyncThunk<
  ITournament[],
  void
>("tournaments/fetchGetAvailableTournaments", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get("/tournaments/available");
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message ||
        "Ошибка получения доступных турниров"
    );
  }
});

export const fetchGetIncognitoAvailableTournaments = createAsyncThunk<
  ITournament[],
  void
>(
  "tournaments/fetchGetIncognitoAvailableTournaments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/tournaments");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка получения турниров"
      );
    }
  }
);

export const fetchGetMyApplicationsTournaments = createAsyncThunk<
  ITournament[],
  void
>(
  "tournaments/fetchGetMyApplicationsTournaments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/tournaments/my-applications");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка получения турниров с заявками"
      );
    }
  }
);

export const fetchGetCreatedTournaments = createAsyncThunk<ITournament[], void>(
  "tournaments/fetchGetCreatedTournaments",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/tournaments/my-tournaments");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка получения созданных турниров"
      );
    }
  }
);

export const fetchGetTournamentApplicationsCount = createAsyncThunk<
  { count: number },
  void
>(
  "tournaments/fetchGetTournamentApplicationsCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        "/tournaments/my-tournaments/applications-count"
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка получения количества заявок"
      );
    }
  }
);

export const fetchGetUserTournamentStatus = createAsyncThunk<
  string | null,
  void
>("tournaments/fetchGetUserTournamentStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get("/user/verification/status");
    return response.data?.status || null;
  } catch (error: any) {
    return null;
  }
});

export const fetchChangeUserTournamentStatus = createAsyncThunk<
  string | null,
  { role: string }
>(
  "tournaments/fetchChangeUserTournamentStatus",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/user/verification/request",
        data
      );
      return response.data?.status || null;
    } catch (error: any) {
      return null;
    }
  }
);

export const fetchCreateTournament = createAsyncThunk<
  ITournament,
  ICreateTournamentData
>(
  "tournaments/fetchCreateTournament",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/tournaments/organizer/create",
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка создания турнира"
      );
    }
  }
);

export const fetchUpdateTournament = createAsyncThunk<
  ITournament,
  { id: number; data: Partial<ICreateTournamentData> }
>(
  "tournaments/fetchUpdateTournament",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/tournaments/organizer/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка обновления турнира"
      );
    }
  }
);

export const fetchDeleteTournament = createAsyncThunk<number, number>(
  "tournaments/fetchDeleteTournament",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/tournaments/organizer/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка удаления турнира"
      );
    }
  }
);

export const fetchApplyTournament = createAsyncThunk<
  void,
  { id: number; data: IApplyTournamentData }
>(
  "tournaments/fetchApplyTournament",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(`/tournaments/${id}/apply`, data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка подачи заявки на турнир"
      );
    }
  }
);

export const fetchCancelTournamentApplication = createAsyncThunk<number, number>(
  "tournaments/fetchCancelTournamentApplication",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/tournaments/${id}/cancel-application`);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка отмены заявки"
      );
    }
  }
);

export const fetchGetTournamentApplications = createAsyncThunk<
  { applications: ITournamentApplication[] },
  number
>(
  "tournaments/fetchGetTournamentApplications",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/tournaments/${id}/organizer/applications`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка получения заявок на турнир"
      );
    }
  }
);

export const fetchChangeApplicationStatus = createAsyncThunk<
  void,
  {
    tournamentId: number;
    type: "single" | "pair";
    applicationId: number;
    status: "approved" | "rejected";
    isPaid: boolean;
  }
>(
  "tournaments/fetchChangeApplicationStatus",
  async (
    { tournamentId, type, applicationId, status, isPaid },
    { rejectWithValue }
  ) => {
    try {
      // Преобразуем статус в формат API: APPROVED или DECLINED
      const apiStatus = status === "approved" ? "APPROVED" : "DECLINED";
      await axiosInstance.put(
        `/tournaments/${tournamentId}/organizer/applications/${type}/${applicationId}`,
        { status: apiStatus, is_paid: isPaid }
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка изменения статуса заявки"
      );
    }
  }
);

export const fetchDeleteApplication = createAsyncThunk<
  void,
  {
    tournamentId: number;
    type: "single" | "pair";
    applicationId: number;
  }
>(
  "tournaments/fetchDeleteApplication",
  async ({ tournamentId, type, applicationId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(
        `/tournaments/${tournamentId}/organizer/applications/${type}/${applicationId}`
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Ошибка удаления заявки"
      );
    }
  }
);

export const fetchMarkTournamentApplicationsAsSeen = createAsyncThunk<
  void,
  { applications: Array<{ id: number; type: string }> }
>(
  "tournaments/fetchMarkTournamentApplicationsAsSeen",
  async (data, { rejectWithValue }) => {
    try {
      await axiosInstance.post(
        "/tournaments/my-tournaments/mark-applications-seen",
        data
      );
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Ошибка отметки заявок как просмотренных"
      );
    }
  }
);
